import { Popup, PopupSettings } from 'components/common/popup/Popup';
import { Box, Stack, Button, Theme } from '@mui/material';
import React, { useEffect, useState } from 'react';
import empty from './images/empty.svg';
import DashboardLayoutIcon from './icon-components/DashboardLayoutIcon';
import ChartsLayoutIcon from './icon-components/ChartsLayoutIcon';
import KPIsAnnouncementsLayoutIcon from './icon-components/KPIsAnnouncementsLayoutIcon';
import KPIsLayoutIcon from './icon-components/KPIsLayoutIcon';
import KPIsTwoTilesLayoutIcon from './icon-components/KPIsTwoTilesLayoutIcon';
import { alpha } from '@mui/material/styles';
import useBundleTranslation from 'i18n';

export const LayoutsPopup = ({ show, setLayout }: { show: boolean; setLayout: (id: number | null) => void }) => {
    const { t } = useBundleTranslation(['app/portal-page-builder']);
    const [showPopup, setShowPopup] = useState(false);

    useEffect(() => {
        setShowPopup(show);
    }, [show]);

    const layouts = [
        { description: 'layouts.0', type: 'image', image: empty, id: null },
        { description: 'layouts.1', type: 'component', Component: DashboardLayoutIcon, id: 1 },
        { description: 'layouts.2', type: 'component', Component: ChartsLayoutIcon, id: 2 },
        { description: 'layouts.3', type: 'component', Component: KPIsAnnouncementsLayoutIcon, id: 3 },
        { description: 'layouts.4', type: 'component', Component: KPIsLayoutIcon, id: 4 },
        { description: 'layouts.5', type: 'component', Component: KPIsTwoTilesLayoutIcon, id: 5 },
    ];

    const popupSettings: PopupSettings = {
        noButtons: true,
        title: t('layouts.create_app'),
    };

    return (
        <Popup
            settings={popupSettings}
            open={showPopup}
            onHide={() => setShowPopup(false)}
            scrollType="body"
            maxWidth="popupXl"
            sx={{
                '.MuiPaper-root': {
                    width: '1176px !important',
                    maxWidth: 'none !important',
                },
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 3,
                }}
            >
                {layouts.map((layout) => {
                    const Icon = layout.Component;

                    return (
                        <Stack key={layout.description} sx={{ position: 'relative' }}>
                            <Box
                                onClick={() => {
                                    if (layout.id) setLayout(layout.id);
                                    setShowPopup(false);
                                }}
                                sx={{
                                    position: 'relative',
                                    borderRadius: 1,
                                    overflow: 'hidden',
                                    cursor: 'pointer',
                                    '&:hover .select-btn': layout.id
                                        ? {
                                              display: 'block',
                                              transform: 'translate(-50%, -50%) scale(1.05)',
                                          }
                                        : null,
                                    '&:hover::after': layout.id
                                        ? {
                                              content: '""',
                                              position: 'absolute',
                                              inset: 0,
                                              backgroundColor: (theme: Theme) =>
                                                  alpha(theme.palette.text.opposite, 0.64),
                                              backdropFilter: 'blur(2px)',
                                              border: '1px solid',
                                              borderRadius: '4px',
                                              borderColor: (theme: Theme) => alpha(theme.palette.primary.main, 0.64),
                                          }
                                        : null,
                                }}
                            >
                                {layout.type === 'image' ? (
                                    <Box
                                        component="img"
                                        src={layout.image}
                                        sx={{
                                            width: '360px',
                                            height: '216px',
                                            display: 'block',
                                            '&:hover': {
                                                backgroundColor: !layout.id
                                                    ? (theme: Theme) => alpha(theme.palette.primary.main, 0.04)
                                                    : null,
                                            },
                                        }}
                                    />
                                ) : (
                                    <Box
                                        sx={{
                                            width: 360,
                                            height: 216,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                        }}
                                    >
                                        {Icon && <Icon width={360} height={216} />}
                                    </Box>
                                )}

                                <Button
                                    className="select-btn"
                                    variant="contained"
                                    sx={{
                                        position: 'absolute',
                                        top: '50%',
                                        left: '50%',
                                        transform: 'translate(-50%, -50%)',
                                        display: 'none',
                                        zIndex: 1,
                                        textTransform: 'none',
                                    }}
                                >
                                    {t('layouts.select_layout')}
                                </Button>
                            </Box>

                            <Box sx={{ pt: 1, fontWeight: 500 }}>{t(layout.description)}</Box>
                        </Stack>
                    );
                })}
            </Box>
        </Popup>
    );
};
